import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import aboutHeroImage from "../../assets/about/abt.webp";
import { contentMap, coreValues } from '../../data/Data';
import { Helmet } from 'react-helmet';

const About = () => {
  const [selected, setSelected] = useState('mission');
  const currentContent = contentMap[selected];
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const fadeSlide = {
    hidden: { opacity: 0, x: 60 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
  };

  const cardVariant = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.2, duration: 0.6 }
    }),
  };

  return (
    <>
      <Helmet>
        <title>About PG Overseas Services | Global Business Solutions Provider</title>
        <meta
          name="description"
          content="PG Overseas Services is a global business solutions provider based in India, offering cross-border consulting, migration, investment, and relocation services. Learn more about our mission, vision, and values."
        />
        <meta
          name="keywords"
          content="PG Overseas Services, global business solutions, cross-border services, international consulting, relocation services India, migration consultants, business expansion, overseas opportunities, expat support"
        />
      </Helmet>
      <motion.section className="font-basefont" >
        {/* Hero Section */}
        <motion.div variants={fadeUp} className="bg-white py-16 lg:py-24 mt-10">
          <div className="max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Empowering Global Success Through Expert Guidance
              </h1>
              <p className="text-gray-700 text-lg leading-relaxed mb-8">
                We provide expert cross-border services, simplifying the complexities of the
                international landscape for individuals and businesses worldwide.
              </p>
              {/* <button className="bg-basecolor text-white font-semibold py-3 px-6 rounded-md hover:bg-textcolor-dark focus:outline-none focus:ring-2 focus:ring-textcolor focus:ring-offset-1">
                Learn More
              </button> */}
            </div>
            <motion.div variants={fadeSlide} className="shadow-lg">
              <img src={aboutHeroImage} alt="About Us" className="w-full rounded-lg h-auto object-cover" />
            </motion.div>
          </div>
        </motion.div>

        {/* Intro Paragraph */}
        <motion.div variants={fadeUp} className="bg-gray-100 py-16 lg:py-24">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12 lg:mb-16">
              <h2 className="text-3xl md:text-4xl font-semibold text-basecolor mb-6">Our Company</h2>
              <p className="text-gray-700 text-lg md:text-xl leading-relaxed">
                PG Overseas Services Private Limited (identified by the Corporate Identification Number
                U85500GJ2023PTC146150) stands as a leading global business solutions provider,
                strategically located in Gujarat, India. Our core purpose is to serve as a vital
                bridge, effectively connecting both individual professionals and diverse businesses
                with the vast array of opportunities that exist across international borders.
                We achieve this by offering a comprehensive suite of expert cross-border services,
                meticulously designed to demystify the often-intricate processes involved in global
                ventures. Our commitment is to simplify these complexities, empowering our clients
                to navigate the international landscape with enhanced confidence and ease,
                ultimately facilitating their success in the global marketplace.
              </p>
            </div>

            {/* Mission/Vision Section */}
            <motion.div variants={fadeUp} className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-6 rounded-xl overflow-hidden shadow-lg bg-white">
              <div className="py-10 px-6 sm:px-10 flex flex-col">
                <div className="mb-6 flex space-x-2">
                  {Object.keys(contentMap)?.map((key) => (
                    <button
                      key={key}
                      onClick={() => setSelected(key)}
                      className={`text-sm font-semibold py-2 px-6 rounded-md focus:outline-none transition-colors duration-300 ${selected === key
                        ? 'bg-basecolor text-white shadow-md'
                        : 'bg-gray-200 text-gray-700 hover:bg-basecolor hover:text-white'
                        }`}
                    >
                      {contentMap[key].title.split(' ')[1]}
                    </button>
                  ))}
                </div>
                <h2 className="text-2xl font-semibold text-basecolor mb-4">{currentContent.title}</h2>
                <p className="text-gray-700 leading-relaxed">{currentContent.text}</p>
              </div>
              {/* Animate Image Change */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={selected}
                  className="overflow-hidden h-96 lg:h-auto"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <video
                    src={currentContent.image}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="inset-0 w-full h-full rounded-md object-cover"
                  />
                  {/* <img
                    src={currentContent.image}
                    alt={currentContent.title}
                    className="inset-0 w-full h-full rounded-md object-cover"
                  /> */}
                </motion.div>
              </AnimatePresence>
            </motion.div>

            {/* Core Values */}
            <motion.section variants={fadeUp} className="py-12">
              <h2 className="text-3xl font-semibold text-gray-800 text-center mb-10">Our Core Values</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {coreValues?.map((value, index) => (
                  <motion.div
                    key={index}
                    custom={index}
                    variants={cardVariant}
                    className="bg-white rounded-lg shadow-md p-8 flex flex-col items-center text-center border-b-4 border-textcolor"
                  >
                    {React.createElement(value.icon, { className: "w-12 h-12 text-basecolor mb-4" })}
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{value.name}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.section>
          </div>
        </motion.div>
      </motion.section>
    </>
  );
};

export default About;
